// client/src/components/FeedbackWidget.tsx
import React, { useState } from 'react';
import { MessageCircle, X, Send, Star } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface FeedbackWidgetProps {
  user?: any;
}

export default function FeedbackWidget({ user }: FeedbackWidgetProps) {
  // Safety check for React context
  if (!React || typeof React.useState !== 'function') {
    return null;
  }
  
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [feedback, setFeedback] = useState({
    type: 'general',
    message: '',
    rating: 0
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000'}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...feedback,
          userId: user?.id,
          page: window.location.pathname
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setTimeout(() => {
          setIsOpen(false);
          setSubmitted(false);
          setFeedback({ type: 'general', message: '', rating: 0 });
        }, 2000);
      }
    } catch (error) {
      console.error('Feedback submission failed:', error);
    }
  };

  if (submitted) {
    return (
      <div className="fixed bottom-4 right-4 bg-green-600 text-white p-4 rounded-lg shadow-lg">
        <p className="font-medium">
          {language === 'st' ? 'Kea leboha!' : 'Thank you!'}
        </p>
        <p className="text-sm">
          {language === 'st' ? 'Maikutlo a hau a bohlokoa ho rona' : 'Your feedback helps us improve'}
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Feedback button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 md:bottom-4 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors z-40"
        title={language === 'st' ? 'Fana ka maikutlo' : 'Give feedback'}
      >
        <MessageCircle className="w-6 h-6" />
      </button>

      {/* Feedback modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {language === 'st' ? 'Fana ka Maikutlo' : 'Share Feedback'}
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'st' ? 'Mofuta' : 'Type'}
                </label>
                <select
                  value={feedback.type}
                  onChange={(e) => setFeedback(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value="general">
                    {language === 'st' ? 'Kakaretso' : 'General'}
                  </option>
                  <option value="bug">
                    {language === 'st' ? 'Phoso' : 'Bug Report'}
                  </option>
                  <option value="feature">
                    {language === 'st' ? 'Kopo ea tšobotsi' : 'Feature Request'}
                  </option>
                  <option value="rating">
                    {language === 'st' ? 'Tekolo' : 'Rating'}
                  </option>
                </select>
              </div>

              {feedback.type === 'rating' && (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {language === 'st' ? 'Tekolo ea hau' : 'Your rating'}
                  </label>
                  <div className="flex space-x-1">
                    {[1, 2, 3, 4, 5].map(star => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFeedback(prev => ({ ...prev, rating: star }))}
                        className={`p-1 ${
                          star <= feedback.rating ? 'text-yellow-500' : 'text-gray-300'
                        }`}
                      >
                        <Star className="w-6 h-6 fill-current" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">
                  {language === 'st' ? 'Molaetsa' : 'Message'}
                </label>
                <textarea
                  value={feedback.message}
                  onChange={(e) => setFeedback(prev => ({ ...prev, message: e.target.value }))}
                  placeholder={
                    language === 'st' 
                      ? 'Re bolelle maikutlo a hau...'
                      : 'Tell us your thoughts...'
                  }
                  className="w-full p-3 border border-gray-300 rounded-lg h-24 resize-none"
                  required
                />
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {language === 'st' ? 'Hlakola' : 'Cancel'}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {language === 'st' ? 'Romela' : 'Send'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}